import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { Upload, Wand2, Image as ImageIcon, Trash2, Edit, Eye, X, Download, Share2, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/ui/footer';
import { Link } from 'wouter';
import type { CartoonCharacter } from '@shared/schema';

// Utility function to check if an image URL is likely to be missing
const isImageUrlLikelyMissing = (url: string): boolean => {
  // Check if the URL contains the problematic timestamp pattern
  return url.includes('1758584696785') || url.includes('1758584697250') || url.includes('1758584697589');
};

export default function CharacterStudio() {
  const { user, hasActiveSubscription } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [characterName, setCharacterName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [newlyGeneratedCharacter, setNewlyGeneratedCharacter] = useState<CartoonCharacter | null>(null);
  const [previewCharacter, setPreviewCharacter] = useState<CartoonCharacter | null>(null);

  // Calculate remaining free generations for non-subscribers
  // Only lifetime and active subscription users have unlimited access, NOT trial users
  const hasUnlimitedAccess = user?.subscriptionStatus === 'lifetime' || user?.subscriptionStatus === 'active';
  const charactersGenerated = user?.charactersGenerated || 0;
  const remainingFreeGenerations = hasUnlimitedAccess ? Infinity : Math.max(0, 3 - charactersGenerated);

  // Query for user's cartoon characters
  const { data: characters = [], isLoading, error } = useQuery({
    queryKey: ['/api/cartoon-characters'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/cartoon-characters');
      const data = await response.json();
      return data.characters || [];
    },
    enabled: !!user
  });

  // Regenerate character using proper OpenAI Vision + DALL-E (no more placeholders)
  const regenerateCharacterMutation = useMutation({
    mutationFn: async (character: CartoonCharacter) => {
      if (!character.originalPhotoUrl) {
        throw new Error('Cannot regenerate character without original photo');
      }
      
      // Get the original photo and regenerate the character properly
      const photoResponse = await fetch(character.originalPhotoUrl);
      const photoBlob = await photoResponse.blob();
      
      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(photoBlob);
      });
      
      const response = await apiRequest('POST', '/api/process', {
        characterName: character.name,
        base64Image: base64,
        prompt: `Create a cartoon character named ${character.name} for a children's storybook.`
      });
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
      toast({
        title: "Character Regenerated!",
        description: "Your character has been regenerated with improved quality.",
      });
    }
  });

  // Ensure characters is always an array
  const safeCharacters = Array.isArray(characters) ? characters : [];

  // Poll for character processing completion
  const pollForCompletion = async (uploadId: string): Promise<void> => {
    const maxPolls = 30; // 30 polls = 60 seconds max
    const pollInterval = 2000; // 2 seconds between polls
    
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
      try {
        const statusResponse = await apiRequest('GET', `/api/character-image/status/${uploadId}`);
        const statusResult = await statusResponse.json();
        
        if (statusResponse.ok && statusResult.success) {
          if (statusResult.status === 'completed') {
            return; // Processing complete
          } else if (statusResult.status === 'failed') {
            throw new Error(statusResult.error || 'Character processing failed');
          }
          // Continue polling if status is still 'processing'
        }
      } catch (error) {
        if (i === maxPolls - 1) {
          // Last attempt, throw the error
          throw error;
        }
        // Continue polling on errors (might be temporary network issues)
      }
    }
    
    throw new Error('Character processing timed out. Please try again.');
  };

  // Generate cartoon character mutation - Robust implementation
  const generateCartoonMutation = useMutation({
    mutationFn: async ({ file, name }: { file: File; name: string }) => {
      setIsGenerating(true);
      
      console.log("Starting character generation:", { fileName: file.name, name });

      try {
        // Convert file to base64 for the proper character generation endpoint
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        console.log("File converted to base64, calling proper character generation endpoint");
        console.log("Base64 length:", base64.length);
        console.log("Character name:", name.trim());
        
        // Check if the base64 data is too large
        if (base64.length > 10 * 1024 * 1024) { // 10MB limit
          throw new Error('Image file is too large. Please try with a smaller image (max 10MB).');
        }

        // Use the proper OpenAI prompt template character generation endpoint
        console.log("Making API request to /api/process...");
        const response = await apiRequest('POST', '/api/process', {
          characterName: name.trim(),
          base64Image: base64,
          prompt: `Create a cartoon character named ${name.trim()} for a children's storybook.`
        }, {
          timeout: 120000 // Increase timeout to 2 minutes for character generation
        });
        console.log("API request completed, response status:", response.status);

        let result;
        try {
          result = await response.json();
        } catch (jsonError) {
          console.error("Failed to parse response JSON:", jsonError);
          throw new Error(`Server returned invalid response: ${response.status} ${response.statusText}`);
        }

        if (!response.ok) {
          console.error("Character generation failed:", result);
          const errorMessage = result?.error || result?.message || `Server error: ${response.status}`;
          throw new Error(errorMessage);
        }

        console.log("Character generated successfully:", result.character);

        return result;
      } catch (error) {
        console.error("Character generation error:", error);
        
        // Provide more specific error messages
        if (error instanceof Error) {
          if (error.message.includes('401') || error.message.includes('Unauthorized')) {
            throw new Error('Please log in to generate characters.');
          } else if (error.message.includes('402') || error.message.includes('subscription')) {
            throw new Error('Premium subscription required for character generation.');
          } else if (error.message.includes('413') || error.message.includes('Request Entity Too Large')) {
            throw new Error('Image file is too large. Please try with a smaller image.');
          } else if (error.message.includes('timeout')) {
            throw new Error('Request timed out. Please check your connection and try again.');
          } else if (error.message.includes('DOMException')) {
            throw new Error('Network error occurred. Please check your connection and try again.');
          }
        }
        
        throw error;
      }
    },
    onSuccess: async (data) => {
      console.log("Character generation success:", data);
      
      toast({
        title: "Character Created!",
        description: "Your cartoon character has been successfully generated and saved to your gallery."
      });
      
      // Set the newly generated character for display
      if (data.character) {
        setNewlyGeneratedCharacter(data.character);
      }
      
      // Refresh cartoon characters to get the newly created character
      await queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
      
      // Refetch user data to update character generation counter
      await queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      
      // Reset form
      setSelectedFile(null);
      setCharacterName('');
      setPreviewUrl(null);
      setIsGenerating(false);
    },
    onError: (error: any) => {
      console.error("Character generation error:", error);
      setIsGenerating(false);
      
      // Provide more specific error messages
      let errorMessage = "Failed to generate cartoon character. Please try again.";
      
           if (error.message) {
             if (error.message.includes('401') || error.message.includes('Unauthorized')) {
               errorMessage = "Please log in to generate characters.";
             } else if (error.message.includes('402') || error.message.includes('subscription')) {
               errorMessage = "Premium subscription required for character generation.";
             } else if (error.message.includes('413') || error.message.includes('Request Entity Too Large')) {
               errorMessage = "Image file is too large. Please try with a smaller image (max 50MB).";
             } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
               errorMessage = "Server error occurred. Please try again in a moment.";
             } else if (error.message.includes('timeout')) {
               errorMessage = "Request timed out. Please check your connection and try again.";
             } else if (error.message.includes('OpenAI') || error.message.includes('AI')) {
               errorMessage = "AI generation failed. Please try again.";
             } else {
               errorMessage = error.message;
             }
           }
      
      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  // Delete character mutation
  const deleteCharacterMutation = useMutation({
    mutationFn: async (characterId: number) => {
      const response = await apiRequest('DELETE', `/api/cartoon-characters/${characterId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Character Deleted",
        description: "Character has been removed from your gallery."
      });
      queryClient.invalidateQueries({ queryKey: ['/api/cartoon-characters'] });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete character. Please try again.",
        variant: "destructive"
      });
    }
  });

  const handleUseInStory = (character: CartoonCharacter) => {
    // Store selected character in localStorage for story generation
    localStorage.setItem('selectedCartoonCharacter', JSON.stringify(character));
    
    // Show confirmation
    toast({
      title: "Character selected!",
      description: `${character.name} will be used in your next story. Go to Create Story to continue.`,
      action: (
        <Link href="/home">
          <Button variant="outline" size="sm">Create Story</Button>
        </Link>
      ),
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast({
          title: "File Too Large",
          description: "Please select an image smaller than 10MB.",
          variant: "destructive"
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File Type",
          description: "Please select an image file.",
          variant: "destructive"
        });
        return;
      }

      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleGenerate = () => {
    if (!selectedFile || !characterName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please select a photo and enter a character name.",
        variant: "destructive"
      });
      return;
    }

    generateCartoonMutation.mutate({ 
      file: selectedFile, 
      name: characterName.trim() 
    });
  };

  const handleDelete = (characterId: number) => {
    deleteCharacterMutation.mutate(characterId);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              Please sign in to access the Character Studio.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Wand2 className="h-8 w-8 text-purple-600" />
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Character Studio
              </h1>
            </div>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Transform your photos into magical cartoon characters for your bedtime stories! 
              Upload a photo and watch as our AI creates a child-friendly storybook illustration.
            </p>
          </div>

          {/* Quota Display */}
          {!hasUnlimitedAccess && (
            <Card className={`mb-8 ${remainingFreeGenerations === 0 ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20' : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'}`}>
              <CardContent className="pt-6">
                <div className="text-center">
                  {remainingFreeGenerations > 0 ? (
                    <>
                      <p className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">
                        🎨 {remainingFreeGenerations} of 3 free character generations remaining
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        <Link href="/subscribe">
                          <Button variant="link" className="text-purple-600 hover:text-purple-700 p-0 h-auto">
                            Upgrade to Premium
                          </Button>
                        </Link>
                        {' '}for unlimited character generation!
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">
                        ❌ You've used all 3 free character generations
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        Subscribe to create unlimited characters and unlock all premium features!
                      </p>
                      <Button asChild className="bg-purple-600 hover:bg-purple-700">
                        <Link href="/subscribe">Upgrade to Premium</Link>
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {hasUnlimitedAccess && (
            <Card className="mb-8 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-lg font-semibold text-green-700 dark:text-green-300">
                    ✨ Unlimited character generations available
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    You have premium access - create as many characters as you like!
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Character Creation Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Create New Character
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      aria-label="Character generation information"
                    >
                      <Info className="h-4 w-4 text-purple-600" />
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 bg-white dark:bg-gray-800">
                    <p className="text-sm">Character generation may take a few minutes. Please be patient while our AI creates your cartoon character.</p>
                  </HoverCardContent>
                </HoverCard>
              </CardTitle>
              <CardDescription>
                Upload a photo and give your character a name to generate a cartoon illustration.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Photo Upload */}
                <div className="space-y-4">
                  <Label htmlFor="photo-upload">Upload Photo</Label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    {previewUrl ? (
                      <div className="space-y-4">
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className="w-32 h-32 object-cover rounded-lg mx-auto"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedFile(null);
                            setPreviewUrl(null);
                          }}
                          data-testid="button-remove-photo"
                        >
                          Remove Photo
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <ImageIcon className="h-12 w-12 text-gray-400 mx-auto" />
                        <div>
                          <Label htmlFor="photo-upload" className="cursor-pointer">
                            <Button variant="outline" asChild data-testid="button-upload-photo">
                              <span>Choose Photo</span>
                            </Button>
                          </Label>
                          <Input
                            id="photo-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                        </div>
                        <p className="text-sm text-gray-500">
                          Upload a clear photo of a person (max 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Character Details */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="character-name">Character Name</Label>
                    <Input
                      id="character-name"
                      value={characterName}
                      onChange={(e) => setCharacterName(e.target.value)}
                      placeholder="Enter character name..."
                      className="mt-2"
                      data-testid="input-character-name"
                    />
                  </div>

                  <div className="pt-4">
                    <Button 
                      onClick={handleGenerate}
                      disabled={!selectedFile || !characterName.trim() || isGenerating}
                      className="w-full"
                      data-testid="button-generate-character"
                    >
                      {isGenerating ? (
                        <>
                          <Wand2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating Character...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          Generate Cartoon Character
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Newly Generated Character Display */}
          {newlyGeneratedCharacter && (
            <Card className="mb-8 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Wand2 className="h-5 w-5" />
                  Character Created Successfully!
                </CardTitle>
                <CardDescription>
                  Here's your new cartoon character. It has been saved to your gallery.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6 items-center">
                  {/* Generated Character Image */}
                  <div className="flex-shrink-0">
                    <div className="w-48 h-48 relative rounded-lg overflow-hidden border-4 border-green-200 dark:border-green-700">
                      {newlyGeneratedCharacter.cartoonImageUrl ? (
                        <img 
                          src={newlyGeneratedCharacter.cartoonImageUrl} 
                          alt={newlyGeneratedCharacter.name}
                          className="w-full h-full object-cover"
                          data-testid="img-generated-character"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <ImageIcon className="h-12 w-12 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Character Details */}
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-2xl font-bold text-green-700 dark:text-green-300 mb-2" data-testid="text-character-name">
                      {newlyGeneratedCharacter.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Your character is ready to star in bedtime stories! You can now use this character when generating new stories.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        onClick={() => setNewlyGeneratedCharacter(null)}
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-100 dark:border-green-600 dark:text-green-300 dark:hover:bg-green-900/30"
                        data-testid="button-dismiss-result"
                      >
                        Create Another Character
                      </Button>
                      <Button 
                        asChild
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="button-go-to-stories"
                      >
                        <a href="/">Generate Story with This Character</a>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Character Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Your Character Gallery
              </CardTitle>
              <CardDescription>
                All your generated cartoon characters. You can use these in your bedtime stories!
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    Failed to load characters. Please refresh the page.
                  </p>
                </div>
              )}
              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : safeCharacters.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-500 mb-2">No Characters Yet</h3>
                  <p className="text-gray-400">Create your first cartoon character to get started!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {safeCharacters.map((character: CartoonCharacter) => (
                    <Card key={character.id} className="overflow-hidden group hover:shadow-lg transition-shadow">
                      <div className="aspect-square relative">
                        {!isImageUrlLikelyMissing(character.cartoonImageUrl) ? (
                          <>
                            <img 
                              src={character.cartoonImageUrl} 
                              alt={character.name}
                              className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                              onClick={() => setPreviewCharacter(character)}
                              onError={(e) => {
                                console.log('Image failed to load:', character.cartoonImageUrl);
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const placeholder = target.nextElementSibling as HTMLElement;
                                if (placeholder) {
                                  placeholder.style.display = 'flex';
                                }
                              }}
                              onLoad={() => {
                                console.log('Image loaded successfully:', character.cartoonImageUrl);
                              }}
                            />
                            {/* Preview overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                              <Button
                                size="sm"
                                variant="secondary"
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewCharacter(character);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Preview
                              </Button>
                            </div>
                          </>
                        ) : null}
                        <div 
                          className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-purple-100 to-teal-100"
                          style={{ display: isImageUrlLikelyMissing(character.cartoonImageUrl) ? 'flex' : 'none' }}
                        >
                          <ImageIcon className="w-16 h-16 mb-2 opacity-50" />
                          <span className="text-sm font-medium">{character.name}</span>
                          <span className="text-xs text-gray-400 mb-2">Image not available</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              regenerateCharacterMutation.mutate(character);
                            }}
                            disabled={regenerateCharacterMutation.isPending}
                            className="text-xs"
                          >
                            {regenerateCharacterMutation.isPending ? 'Generating...' : 'Generate Placeholder'}
                          </Button>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-medium mb-2" data-testid={`text-character-name-${character.id}`}>
                          {character.name}
                        </h3>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setPreviewCharacter(character)}
                            className="flex-1"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid={`button-view-${character.id}`}>
                                <Edit className="h-4 w-4 mr-1" />
                                Details
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>{character.name}</DialogTitle>
                                <DialogDescription>
                                  Created on {new Date(character.createdAt).toLocaleDateString()}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>Cartoon Character</Label>
                                  <div className="relative">
                                    {!isImageUrlLikelyMissing(character.cartoonImageUrl) ? (
                                      <img 
                                        src={character.cartoonImageUrl} 
                                        alt={`${character.name} cartoon`}
                                        className="w-full rounded-lg mt-2"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const placeholder = target.nextElementSibling as HTMLElement;
                                          if (placeholder) {
                                            placeholder.style.display = 'flex';
                                          }
                                        }}
                                      />
                                    ) : null}
                                    <div 
                                      className="w-full h-48 flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-purple-100 to-teal-100 rounded-lg mt-2"
                                      style={{ display: isImageUrlLikelyMissing(character.cartoonImageUrl) ? 'flex' : 'none' }}
                                    >
                                      <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                                      <span className="text-sm font-medium">{character.name}</span>
                                      <span className="text-xs text-gray-400 mb-2">Image not available</span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => regenerateCharacterMutation.mutate(character)}
                                        disabled={regenerateCharacterMutation.isPending}
                                        className="text-xs"
                                      >
                                        {regenerateCharacterMutation.isPending ? 'Generating...' : 'Generate Placeholder'}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                {character.originalPhotoUrl && (
                                  <div>
                                    <Label>Original Photo</Label>
                                    <div className="relative">
                                      <img 
                                        src={character.originalPhotoUrl} 
                                        alt={`${character.name} original`}
                                        className="w-full rounded-lg mt-2"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const placeholder = target.nextElementSibling as HTMLElement;
                                          if (placeholder) {
                                            placeholder.style.display = 'flex';
                                          }
                                        }}
                                      />
                                      <div 
                                        className="w-full h-48 flex flex-col items-center justify-center text-gray-500 bg-gray-100 rounded-lg mt-2"
                                        style={{ display: 'none' }}
                                      >
                                        <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                                        <span className="text-sm font-medium">Original Photo</span>
                                        <span className="text-xs text-gray-400">Not available</span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid={`button-delete-${character.id}`}>
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Character</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{character.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(character.id)}
                                  data-testid={`button-confirm-delete-${character.id}`}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </div>

      {/* Character Preview Modal */}
      <Dialog open={!!previewCharacter} onOpenChange={() => setPreviewCharacter(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              {previewCharacter?.name}
            </DialogTitle>
            <DialogDescription>
              Created on {previewCharacter ? new Date(previewCharacter.createdAt).toLocaleDateString() : ''}
            </DialogDescription>
          </DialogHeader>
          
          {previewCharacter && (
            <div className="space-y-4">
              {/* Character Image */}
              <div className="relative">
                {!isImageUrlLikelyMissing(previewCharacter.cartoonImageUrl) ? (
                  <img 
                    src={previewCharacter.cartoonImageUrl} 
                    alt={previewCharacter.name}
                    className="w-full max-h-96 object-contain rounded-lg border"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className="w-full h-96 flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-purple-100 to-teal-100 rounded-lg border"
                  style={{ display: isImageUrlLikelyMissing(previewCharacter.cartoonImageUrl) ? 'flex' : 'none' }}
                >
                  <ImageIcon className="w-20 h-20 mb-4 opacity-50" />
                  <span className="text-lg font-medium">{previewCharacter.name}</span>
                  <span className="text-sm text-gray-400 mb-4">Image not available</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => regenerateCharacterMutation.mutate(previewCharacter)}
                    disabled={regenerateCharacterMutation.isPending}
                  >
                    {regenerateCharacterMutation.isPending ? 'Generating...' : 'Generate Placeholder'}
                  </Button>
                </div>
              </div>

              {/* Character Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-500">Character Name</Label>
                  <p className="text-lg font-semibold">{previewCharacter.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">Created</Label>
                  <p className="text-sm">{new Date(previewCharacter.createdAt).toLocaleString()}</p>
                </div>
              </div>

              {/* Character Traits */}
              {previewCharacter.characterTraits && Object.keys(previewCharacter.characterTraits).length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-500">Character Traits</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(previewCharacter.characterTraits).map(([key, value]) => (
                      <span 
                        key={key}
                        className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs"
                      >
                        {key}: {String(value)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  onClick={() => {
                    // Copy image URL to clipboard
                    if (previewCharacter.cartoonImageUrl) {
                      navigator.clipboard.writeText(previewCharacter.cartoonImageUrl);
                      toast({
                        title: "Copied!",
                        description: "Character image URL copied to clipboard.",
                      });
                    }
                  }}
                  variant="outline"
                  size="sm"
                >
                  <Share2 className="h-4 w-4 mr-1" />
                  Copy URL
                </Button>
                <Button 
                  onClick={() => {
                    // Download image
                    if (previewCharacter.cartoonImageUrl) {
                      const link = document.createElement('a');
                      link.href = previewCharacter.cartoonImageUrl;
                      link.download = `${previewCharacter.name}-character.png`;
                      link.click();
                    }
                  }}
                  variant="outline"
                  size="sm"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </Button>
                <Button 
                  onClick={() => handleUseInStory(previewCharacter)}
                  className="flex-1"
                >
                  Use in Story
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}